/**
 * Scenario-based interactive system — canonical data model definitions.
 *
 * TypeScript interfaces live in docs/interactive-scene-rewrite.md (section 3).
 * This file is the runtime JS counterpart: factory functions + default shapes.
 */
import i18n from "i18next";

// ---------------------------------------------------------------------------
// Element type registry
// ---------------------------------------------------------------------------

export const ELEMENT_TYPES = [
  "visualtext",
  "visualimage",
  "videocard",
  "flipcard",
  "truefalse",
  "mcq",
];

export const ACTION_TYPES = ["navigate", "alert", "set_variable", "end_scenario"];

export const VARIABLE_TYPES = ["number", "boolean", "string"];

// ---------------------------------------------------------------------------
// Factory helpers — produce minimal valid objects with stable ids
// ---------------------------------------------------------------------------

const CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";

export function makeId(len = 10) {
  let id = "";
  for (let i = 0; i < len; i++) {
    id += CHARS[Math.floor(Math.random() * CHARS.length)];
  }
  return id;
}

export function makeAction(overrides = {}) {
  return {
    id: makeId(),
    type: "navigate",
    targetSceneId: null,
    alertMessage: null,
    variableName: null,
    variableValue: null,
    variableOp: "set",
    condition: null,
    ...overrides,
  };
}

export function makeCondition(overrides = {}) {
  return {
    variableName: "",
    operator: "==",
    value: 0,
    ...overrides,
  };
}

export function makeButton(overrides = {}) {
  return {
    id: makeId(),
    label: i18n.t("InteractiveScenario.Default.Continue") || "Devam",
    style: "primary",
    actions: [],
    ...overrides,
  };
}

export function makeChoice(overrides = {}) {
  return {
    id: makeId(),
    text: "",
    actions: [],
    ...overrides,
  };
}

// ---------------------------------------------------------------------------
// defaultForElement — minimal valid element for each type
// ---------------------------------------------------------------------------

export function defaultForElement(type, positionOverride) {
  const defaultPos = positionOverride || { x: 5, y: 5 };
  const base = {
    id: makeId(),
    type,
    title: "",
    showImage: false,
    position: defaultPos,
    size: { width: 300 },
    hidden: false,
  };

  switch (type) {
    case "visualtext":
      return {
        ...base,
        title: i18n.t("InteractiveScenario.Type.VisualText") || "Metin Kartı",
        content: "",
        image: null,
        imageAspectRatio: "16:9",
        imagePosition: "top",
        buttons: [makeButton()],
      };

    case "flipcard":
      return {
        ...base,
        title: i18n.t("InteractiveScenario.Type.FlipCard") || "Çift Yüzlü Kart",
        frontTitle: i18n.t("InteractiveScenario.Default.Title") || "Başlık",
        frontText:
          i18n.t("InteractiveScenario.Default.FrontTextHint") ||
          "Kartın ön yüzüne bir ipucu veya soru yazın. Kullanıcı kartı çevirerek cevabı öğrenir.",
        frontBg: "#1e3a5f",
        frontTextColor: "#ffffff",
        frontImage: null,
        backTitle: i18n.t("InteractiveScenario.Default.BackTitle") || "Cevap",
        backText:
          i18n.t("InteractiveScenario.Default.BackTextHint") ||
          "Kartın arka yüzüne açıklamayı veya doğru cevabı yazın.",
        backBg: "#0d5c3a",
        backTextColor: "#ffffff",
        backImage: null,
        flipActions: [],
        backButtonLabel: i18n.t("InteractiveScenario.Default.Continue") || "Devam",
        backActions: [],
      };

    case "truefalse":
      return {
        ...base,
        title: i18n.t("InteractiveScenario.Type.TrueFalse") || "Doğru / Yanlış Sorusu",
        question:
          i18n.t("InteractiveScenario.Default.QuestionHint") ||
          "Buraya sorunuzu yazın. Kullanıcı Doğru veya Yanlış seçeneğini seçerek cevap verir.",
        image: null,
        correctAnswer: true,
        trueLabel: i18n.t("InteractiveScenario.Option.True") || "Doğru",
        falseLabel: i18n.t("InteractiveScenario.Option.False") || "Yanlış",
        correctActions: [],
        incorrectActions: [],
        explanation:
          i18n.t("InteractiveScenario.Default.ExplanationHint") ||
          "Doğru cevabın açıklamasını buraya yazın.",
      };

    case "mcq":
      return {
        ...base,
        title: i18n.t("InteractiveScenario.Type.MCQ") || "Çoktan Seçmeli Soru",
        question:
          i18n.t("InteractiveScenario.Default.MCQQuestionHint") ||
          "Buraya sorunuzu yazın. Aşağıdaki seçeneklerden doğru olanı işaretleyin.",
        image: null,
        choices: [
          makeChoice({ text: i18n.t("InteractiveScenario.Default.Choice1") || "Birinci seçenek" }),
          makeChoice({ text: i18n.t("InteractiveScenario.Default.Choice2") || "İkinci seçenek" }),
          makeChoice({ text: i18n.t("InteractiveScenario.Default.Choice3") || "Üçüncü seçenek" }),
        ],
        explanation:
          i18n.t("InteractiveScenario.Default.ExplanationHint") ||
          "Doğru cevabın açıklamasını buraya yazın.",
      };

    case "visualimage":
      return {
        ...base,
        title: i18n.t("InteractiveScenario.Type.VisualImage") || "Görsel Kart",
        imageUrl: "",
        caption: "",
        captionAlign: "left",
        imageActions: [],
        buttons: [makeButton()],
      };

    case "videocard":
      return {
        ...base,
        title: i18n.t("InteractiveScenario.Type.VideoCard") || "Video Kart",
        videoUrl: "",
        autoPlay: false,
        caption: "",
        captionAlign: "left",
        buttons: [makeButton()],
      };

    default:
      throw new Error(`[InteractivePlugin] Unknown element type: "${type}"`);
  }
}

// ---------------------------------------------------------------------------
// defaultForVariable
// ---------------------------------------------------------------------------

export function defaultForVariable(overrides = {}) {
  return {
    name: "",
    type: "number",
    defaultValue: 0,
    ...overrides,
  };
}

// ---------------------------------------------------------------------------
// defaultForScene
// ---------------------------------------------------------------------------

export function defaultForScene(overrides = {}) {
  return {
    id: makeId(),
    title: i18n.t("InteractiveScenario.Default.NewScene") || "Yeni Sahne",
    description: "",
    isStart: false,
    position: { x: 100, y: 100 },
    bgColor: "#1a1a2e",
    bgPattern: "none",
    bgImage: null,
    textColor: "#ffffff",
    elements: [],
    ...overrides,
  };
}

// ---------------------------------------------------------------------------
// emptyScenario — used as InteractivePlugin initialState.scenario
// ---------------------------------------------------------------------------

export function emptyScenario(overrides = {}) {
  const startScene = defaultForScene({
    isStart: true,
    title: i18n.t("InteractiveScenario.Label.StartTitleCase") || "Başlangıç",
  });
  return {
    id: makeId(),
    name: i18n.t("InteractiveScenario.Default.NewScenario") || "Yeni Senaryo",
    description: "",
    startSceneId: startScene.id,
    variables: [],
    scenes: [startScene],
    ...overrides,
  };
}
